"use client"

import { useCallback, useEffect, useRef, useState, type FormEvent } from "react"
import type { StripeExpressCheckoutElementConfirmEvent } from "@stripe/stripe-js"
import { ExpressCheckoutElement, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js"
import { PayPalButtons, usePayPalScriptReducer } from "@paypal/react-paypal-js"
import { CheckCircle2, ShieldCheck } from "lucide-react"

import { cn, normalizeEmailKey } from "@/lib/utils"
import { SectionEyebrow } from "@/components/marketing/section-eyebrow"
import { MarketingLink, marketingActionClass } from "@/components/marketing/marketing-action"
import { Input } from "@/components/ui/input"
import {
  formatAmount,
  isLikelyEmail,
  paymentMethodOrderFor,
  stampPolicyAgreementWithRetry,
  type DeferredStart,
  type OfferCopy,
} from "./checkout-shared"

/** Where the buyer lands once his form resolves in-page. The client secret
 *  rides along under Stripe's own redirect param name, so the status endpoint
 *  can require possession of it, not just the id. A SetupIntent (a weekly plan
 *  that starts when his block ends, nothing charged today) uses Stripe's
 *  setup_intent names, the same ones Stripe appends after an off-site step. */
function returnUrlFor(intentKind: "payment" | "setup", id: string, clientSecret: string): string {
  const prefix = intentKind === "setup" ? "setup_intent" : "payment_intent"
  return `/pay/return?${prefix}=${encodeURIComponent(id)}&${prefix}_client_secret=${encodeURIComponent(clientSecret)}`
}

/** Policy links inside the light payment panel (the order card's white-text
 *  version lives in order-summary.tsx). */
const POLICY_LINK_CLASS =
  "inline min-h-0 border-0 p-0 text-[var(--color-ink)] underline hover:bg-transparent hover:text-[var(--color-vermilion)]"

/** A quiet "Or" rule between the wallet express row and the card form. */
function OrDivider() {
  return (
    <div className="flex items-center gap-3">
      <span aria-hidden className="h-px flex-1 bg-[var(--color-rule)]" />
      <span className="text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-[var(--color-ink-soft)]">
        Or
      </span>
      <span aria-hidden className="h-px flex-1 bg-[var(--color-rule)]" />
    </div>
  )
}

/** Wallet express-checkout row (Apple Pay / Google Pay / Link) confirming
 * against the SAME PaymentIntent the card form below confirms against.
 * Collapses itself (no row, no divider) when Stripe reports no available
 * wallet for this browser/device — never an empty "Or" floating alone.
 * `agreed` gates it exactly like the card-form Pay button below: Apple Pay /
 * Google Pay / Link must never be able to complete a purchase while the
 * required policy-agreement checkbox is unticked (found live — the element
 * previously had zero reference to `agreed` at all). Two layers: the wrapper
 * is visually disabled + non-interactive until `agreed`, and `handleConfirm`
 * itself refuses to submit/confirm even if a click somehow reaches it. */
export function ExpressCheckoutRow({
  paymentIntentId,
  clientSecret,
  agreed,
  onError,
  intentKind = "payment",
}: {
  paymentIntentId: string
  clientSecret: string
  // Already combines the checkbox AND (for $3,000+) a valid signed name —
  // see the `canPay` derivation in ReadyCheckout below.
  agreed: boolean
  onError: (message: string) => void
  /** "setup" when nothing is charged today: the wallet saves the card on the
   *  subscription's SetupIntent instead of paying. */
  intentKind?: "payment" | "setup"
}) {
  const stripe = useStripe()
  const elements = useElements()
  const [visible, setVisible] = useState(true)

  const handleConfirm = useCallback(
    async (event: StripeExpressCheckoutElementConfirmEvent) => {
      if (!stripe || !elements) return

      if (!agreed) {
        event.paymentFailed({ reason: "fail", message: "Please agree to the policies (and sign, if required) first." })
        onError("Please check the agreement box above before paying.")
        return
      }

      // Same best-effort recordkeeping the card-form path fires before
      // confirming — see stampPolicyAgreementWithRetry above.
      void stampPolicyAgreementWithRetry(paymentIntentId, clientSecret)

      const { error: submitError } = await elements.submit()
      if (submitError) {
        event.paymentFailed({ reason: "fail", message: submitError.message })
        onError(submitError.message || "Your payment didn't go through. Please try again.")
        return
      }

      if (intentKind === "setup") {
        const { error: setupError, setupIntent } = await stripe.confirmSetup({
          elements,
          confirmParams: { return_url: `${window.location.origin}/pay/return` },
          redirect: "if_required",
        })
        if (setupError) {
          event.paymentFailed({ reason: "fail", message: setupError.message })
          onError(setupError.message || "Your payment didn't go through. Please try again.")
          return
        }
        window.location.href = returnUrlFor("setup", setupIntent?.id ?? paymentIntentId, clientSecret)
        return
      }

      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/pay/return`,
          // Lowercased and trimmed like every other email key: a wallet hands
          // back the address as the buyer saved it ("MJS100B@gmail.com"), and
          // Stripe's customer email lookup is case-sensitive, so a verbatim
          // receipt email can never be matched back to his Customer.
          ...(event.billingDetails?.email
            ? { receipt_email: normalizeEmailKey(event.billingDetails.email) }
            : {}),
        },
        redirect: "if_required",
      })

      if (error) {
        event.paymentFailed({ reason: "fail", message: error.message })
        onError(error.message || "Your payment didn't go through. Please try again.")
        return
      }

      const id = paymentIntent?.id ?? paymentIntentId
      // payment_intent_client_secret matches Stripe's own off-site-redirect
      // query param name — the status endpoint requires it (the same
      // possession proof /consent already required) so a bare pi_… id, which
      // is not a secret and ends up in browser history and Referer chains,
      // cannot be replayed against a stranger's status lookup on its own.
      window.location.href = returnUrlFor("payment", id, clientSecret)
    },
    [stripe, elements, paymentIntentId, clientSecret, onError, agreed, intentKind],
  )

  if (!visible) return null

  return (
    <div className="flex flex-col gap-4">
      {/* The "Or" leads INTO the wallets: they sit UNDER the bank-first Payment
          Element, so bank is what a buyer sees first. The unlock prerequisite
          is still stated ONCE, above the whole payment area, never repeated per
          method. */}
      <OrDivider />
      <div
        aria-disabled={!agreed}
        className={cn(!agreed && "pointer-events-none opacity-40 grayscale")}
      >
        <ExpressCheckoutElement
          onConfirm={handleConfirm}
          onReady={(event) => {
            if (!event.availablePaymentMethods) setVisible(false)
          }}
          options={{ emailRequired: true, buttonHeight: 48 }}
        />
      </div>
    </div>
  )
}

type PayPalOptionStatus = "idle" | "creating" | "capturing" | "error"

/** What /api/paypal/capture-order needs to capture one approved order: the
 *  PaymentIntent it was opened against (Stripe-backed), or the standalone
 *  checkout reference and the secret create-order returned once. */
export type PayPalCaptureRequest =
  | { orderId: string; paymentIntentId: string; clientSecret: string }
  | { orderId: string; checkoutId: string; checkoutSecret: string }

export type PayPalResult = {
  paymentStatus: "completed" | "pending"
  email: string | null
  returnPath: string | null
  retry?: PayPalCaptureRequest
}

/** Where a PayPal order comes from. Stripe-backed: the order is priced from
 *  the PaymentIntent the card form uses. Standalone (Stripe cannot take
 *  payments): create-order prices the plan on the server from our own catalog,
 *  and no PaymentIntent exists. */
export type PayPalOrderSource =
  | { kind: "stripe"; paymentIntentId: string; clientSecret: string }
  | { kind: "standalone"; planId: string; request: () => Record<string, unknown> }

/** create-order could not start a PayPal order: `status` is its HTTP answer,
 *  or undefined when the call never got one. */
export type PayPalStartFailure = { status: number | undefined; message: string }

async function requestPayPalCapture(retry: PayPalCaptureRequest) {
  const res = await fetch("/api/paypal/capture-order", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(retry),
  })
  const json = await res.json().catch(() => null)
  return { res, json }
}

/** Captures one approved order, retrying the SAME order with the server's
 *  stable capture idempotency key when a response is lost or ambiguous, and
 *  never creating a new one. Shared by the Stripe-backed and standalone
 *  buttons, which differ only in what the capture call carries. */
async function capturePayPalApproval(
  retry: PayPalCaptureRequest,
  handlers: {
    onError: (message: string) => void
    onSuccess: (result: PayPalResult) => void
    setStatus: (status: PayPalOptionStatus) => void
  },
) {
  const { onError, onSuccess, setStatus } = handlers
  let lastJson: Record<string, unknown> | null = null
  let ambiguous = false
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const { res, json } = await requestPayPalCapture(retry)
      lastJson = json && typeof json === "object" ? json : null
      if (!res.ok) {
        if (ambiguous && json?.terminal !== true) break
        setStatus("error")
        onError(
          json?.error ||
            "PayPal did not start this payment. Refresh checkout before trying another payment method.",
        )
        return
      }
      if (res.status === 200 && json?.status === "COMPLETED") {
        onSuccess({
          paymentStatus: "completed",
          email: typeof json.email === "string" ? json.email : null,
          returnPath: typeof json.returnPath === "string" ? json.returnPath : null,
        })
        return
      }
      if (res.status !== 202) {
        setStatus("error")
        onError("PayPal has not confirmed this payment. Do not submit another payment.")
        return
      }
      ambiguous = true
    } catch {
      // A lost response is ambiguous. Retry the same order with the
      // same server-side capture idempotency key, never a new order.
      ambiguous = true
    }
    if (attempt < 2) {
      await new Promise((resolve) => setTimeout(resolve, 700 * (attempt + 1)))
    }
  }

  onSuccess({
    paymentStatus: "pending",
    email: typeof lastJson?.email === "string" ? lastJson.email : null,
    returnPath: typeof lastJson?.returnPath === "string" ? lastJson.returnPath : null,
    retry,
  })
}

/** Creates the PayPal order only after the buyer accepts the policies. The
 * server reads the final amount from the same PaymentIntent the card form uses,
 * so a promo reprice cannot leave PayPal holding a stale total. */
/** The "Or" divider leads INTO the button here, as it does for the wallet row,
 * because both are options after the Payment Element, not separators before
 * it. */
export function PayPalCheckoutOption({
  source,
  amountCents,
  currency,
  agreed,
  onError,
  onSuccess,
  onStartFailed,
  divider = true,
}: {
  source: PayPalOrderSource
  amountCents: number
  currency: string
  agreed: boolean
  onError: (message: string) => void
  onSuccess: (result: PayPalResult) => void
  /** Standalone only: create-order could not start an order, so the page can
   *  route the buyer to sign in or to the fallback form. */
  onStartFailed?: (failure: PayPalStartFailure) => void
  /** The "Or" rule that leads into the button from the card form above it;
   *  off when PayPal is the only way to pay. */
  divider?: boolean
}) {
  const [status, setStatus] = useState<PayPalOptionStatus>("idle")
  // A standalone order's capture credentials, kept from create until approve.
  const standaloneCapture = useRef<{ orderId: string; checkoutId: string; checkoutSecret: string } | null>(null)
  // Set when create-order refused, so PayPal's own error callback for the same
  // click does not replace that specific answer with the generic one.
  const standaloneStartRefused = useRef(false)
  const reRenderKey = source.kind === "stripe" ? source.paymentIntentId : source.planId

  if (source.kind === "standalone") {
    // Vertical, so PayPal paints every funding source it offers this buyer,
    // its own debit or credit card button included; the horizontal layout
    // stops at two buttons, which left a buyer with no PayPal account no way
    // to pay. Every button runs the same createOrder and onApprove.
    return (
      <div className="flex flex-col gap-4">
        {divider && <OrDivider />}
        <PayPalButtons
          style={{ layout: "vertical", height: 48, label: "paypal" }}
          disabled={status === "creating" || status === "capturing"}
          forceReRender={[reRenderKey, amountCents, currency]}
          createOrder={async () => {
            setStatus("creating")
            standaloneCapture.current = null
            standaloneStartRefused.current = false
            let res: Response | null = null
            let json: Record<string, unknown> | null = null
            try {
              res = await fetch("/api/paypal/create-order", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ...source.request(), planId: source.planId }),
              })
              json = await res.json().catch(() => null)
            } catch {
              res = null
            }
            if (
              !res?.ok ||
              typeof json?.orderId !== "string" ||
              typeof json?.checkoutId !== "string" ||
              typeof json?.checkoutSecret !== "string"
            ) {
              const message =
                typeof json?.error === "string" ? json.error : "PayPal couldn't start this payment. Please try again."
              setStatus("error")
              standaloneStartRefused.current = true
              onError(message)
              onStartFailed?.({ status: res?.status, message })
              throw new Error(message)
            }
            standaloneCapture.current = {
              orderId: json.orderId,
              checkoutId: json.checkoutId,
              checkoutSecret: json.checkoutSecret,
            }
            setStatus("idle")
            return json.orderId
          }}
          onApprove={async (data) => {
            const created = standaloneCapture.current
            if (!created || created.orderId !== data.orderID) {
              setStatus("error")
              onError("PayPal couldn't complete this step. If you approved the payment, do not pay again; try the PayPal confirmation again.")
              return
            }
            setStatus("capturing")
            await capturePayPalApproval(
              { orderId: data.orderID, checkoutId: created.checkoutId, checkoutSecret: created.checkoutSecret },
              { onError, onSuccess, setStatus },
            )
          }}
          onError={() => {
            setStatus("error")
            if (standaloneStartRefused.current) return
            onError(
              "PayPal couldn't complete this step. If you approved the payment, do not pay again; try the PayPal confirmation again.",
            )
          }}
          onCancel={() => {
            setStatus("idle")
          }}
        />
      </div>
    )
  }

  const { paymentIntentId, clientSecret } = source
  return (
    <div className="flex flex-col gap-4">
      {divider && <OrDivider />}
      <div aria-disabled={!agreed} className={cn(!agreed && "pointer-events-none opacity-40 grayscale")}>
        <PayPalButtons
          style={{ layout: "horizontal", height: 48, label: "paypal", tagline: false }}
          disabled={!agreed || status === "creating" || status === "capturing"}
          forceReRender={[reRenderKey, amountCents, currency]}
          createOrder={async () => {
            if (!agreed) {
              const message = "Please check the agreement box above before paying with PayPal."
              onError(message)
              throw new Error(message)
            }

            setStatus("creating")
            const stamped = await stampPolicyAgreementWithRetry(
              paymentIntentId,
              clientSecret,
                        )
            if (!stamped) {
              const message = "We couldn't record your agreement. Please try PayPal again."
              setStatus("error")
              onError(message)
              throw new Error(message)
            }

            const res = await fetch("/api/paypal/create-order", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                paymentIntentId,
                clientSecret,
              }),
            })
            const json = await res.json().catch(() => null)
            if (!res.ok || typeof json?.orderId !== "string") {
              const message = json?.error || "PayPal couldn't start this payment. Please try again."
              setStatus("error")
              onError(message)
              throw new Error(message)
            }
            setStatus("idle")
            return json.orderId
          }}
          onApprove={async (data) => {
            if (!agreed) {
              onError("Please check the agreement box above before paying with PayPal.")
              return
            }
            setStatus("capturing")
            await capturePayPalApproval(
              { orderId: data.orderID, paymentIntentId, clientSecret },
              { onError, onSuccess, setStatus },
            )
          }}
          onError={() => {
            setStatus("error")
            onError(
              "PayPal couldn't complete this step. If you approved the payment, do not pay again; try the PayPal confirmation again.",
            )
          }}
          onCancel={() => {
            setStatus("idle")
          }}
        />
      </div>
    </div>
  )
}

/** Right column: contact information first (so the email ask feels necessary,
 * never arbitrary), one unlock message, then Stripe's Payment Element (bank
 * first at every amount) with our own submit button — the button naming the
 * OUTCOME of the click, with a small what-you're-paying-for block above it —
 * then the wallet express row, and finally the optional PayPal button, last at
 * every price. Must render inside <Elements>. Light (matches the Stripe
 * reference's right-hand payment panel). */
export function PaymentPanel({
  paymentIntentId,
  clientSecret,
  amountCents,
  currency,
  agreed,
  email,
  onEmailChange,
  paypalReady,
  onPaypalSuccess,
  recurringInterval,
  offer,
  intentKind = "payment",
  deferredStart = null,
  onPaymentElementLoadError,
  loadFailureNotice = null,
}: {
  paymentIntentId: string
  clientSecret: string
  amountCents: number
  currency: string
  recurringInterval: string | null
  /** Stripe's Payment Element reported that it could not load its form. */
  onPaymentElementLoadError: (message: string) => void
  /** Set once the payment form has failed to load: it takes the place of the
   *  form, its Pay button and the Stripe wallet row, none of which can work
   *  without Stripe. PayPal, which does not go through Stripe's script, stays. */
  loadFailureNotice?: React.ReactNode
  // Already combines the checkbox AND (for $3,000+) a valid signed name —
  // see the `canPay` derivation in ReadyCheckout below.
  agreed: boolean
  /** The contact email, held by ReadyCheckout so a Try again that remounts
   *  this panel keeps what he typed. */
  email: string
  onEmailChange: (email: string) => void
  paypalReady: boolean
  onPaypalSuccess: (result: PayPalResult) => void
  offer: OfferCopy
  /** "setup" when nothing is charged today: the form saves his card on the
   *  subscription's SetupIntent (confirmSetup) for the first weekly charge. */
  intentKind?: "payment" | "setup"
  /** A weekly plan that starts when his prepaid block ends. */
  deferredStart?: DeferredStart | null
}) {
  const stripe = useStripe()
  const elements = useElements()
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState("")

  const emailValid = isLikelyEmail(email)

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault()
      if (!stripe || !elements || !agreed || !emailValid) return

      setSubmitting(true)
      setFormError("")

      // Best-effort recordkeeping — stamps the policy-agreement timestamp (and,
      // for $3,000+ checkouts, the typed signed name) on the PaymentIntent
      // (retried; a total failure is reported to PostHog instead of swallowed,
      // see stampPolicyAgreementWithRetry above). Never blocks payment on this
      // failing; the real gate is the disabled button below, not this call.
      void stampPolicyAgreementWithRetry(paymentIntentId, clientSecret)

      // Required now that ExpressCheckoutElement is also mounted alongside
      // PaymentElement in the same <Elements> instance — Stripe's documented
      // side-by-side pattern for the two Elements.
      const { error: submitError } = await elements.submit()
      if (submitError) {
        setFormError(submitError.message || "Your payment didn't go through. Please try again.")
        setSubmitting(false)
        return
      }

      // Nothing is charged today on a weekly plan that starts when his block
      // ends: the card is saved on the subscription's SetupIntent, and the
      // subscription charges it on the first weekly date.
      if (intentKind === "setup") {
        const { error: setupError, setupIntent } = await stripe.confirmSetup({
          elements,
          confirmParams: { return_url: `${window.location.origin}/pay/return` },
          redirect: "if_required",
        })
        if (setupError) {
          setFormError(setupError.message || "Your payment didn't go through. Please try again.")
          setSubmitting(false)
          return
        }
        window.location.href = returnUrlFor("setup", setupIntent?.id ?? paymentIntentId, clientSecret)
        return
      }

      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/pay/return`,
          receipt_email: normalizeEmailKey(email),
        },
        redirect: "if_required",
      })

      if (error) {
        setFormError(error.message || "Your payment didn't go through. Please try again.")
        setSubmitting(false)
        return
      }

      // A redirect-based method (Klarna, Affirm, some bank flows) already
      // navigated the browser away before this line ever runs. This only
      // fires for methods that resolved in-page (cards, most instant
      // methods) — send the buyer to the same confirmation page either way.
      const id = paymentIntent?.id ?? paymentIntentId
      // See the sibling handler above: the client secret rides along so the
      // status endpoint can require possession of it, not just the id.
      window.location.href = returnUrlFor("payment", id, clientSecret)
    },
    [stripe, elements, agreed, emailValid, email, paymentIntentId, clientSecret, intentKind],
  )

  // A weekly plan that starts when his block ends names its weekly price on
  // the button, never the $0 due today: the button states what he agrees to
  // pay, and the order summary beside it says when it starts.
  const labelCents = deferredStart ? deferredStart.recurringAmount : amountCents
  const payLabel = offer.payVerbLabel
    ? `${offer.payVerbLabel} · ${formatAmount(labelCents, currency)}`
    : recurringInterval
      ? `Pay ${formatAmount(labelCents, currency)} ${recurringInterval}`
      : `Pay ${formatAmount(labelCents, currency)}`

  return (
    <div className="flex flex-col gap-5 rounded-[var(--radius-lg)] border border-[var(--color-rule)] bg-white p-6 md:p-7">
      {/* Email BEFORE payment methods (owner point 8): asking for it after
          the buyer has thought about payment feels arbitrary; before, it
          reads as the address the receipt and next steps genuinely need. */}
      <div className="flex flex-col gap-2">
        <SectionEyebrow size="sm" as="span">
          Contact information
        </SectionEyebrow>
        <label htmlFor="checkout-email" className="text-[0.82rem] font-semibold text-[var(--color-ink)]">
          Email
        </label>
        <Input
          id="checkout-email"
          type="email"
          inputMode="email"
          autoComplete="email"
          value={email}
          onChange={(e) => onEmailChange(e.target.value)}
          placeholder="you@email.com"
          aria-invalid={email.length > 0 && !emailValid}
        />
        <p className="text-[0.82rem] leading-[1.4] text-[var(--color-ink-muted)]">
          We&rsquo;ll send your receipt and next steps here.
        </p>
      </div>


      <SectionEyebrow size="sm" as="span">
        Payment
      </SectionEyebrow>

      {/* The single unlock message (owner point 7) — the same prerequisite
          used to be explained separately under the wallets and under PayPal. */}
      {!agreed && (
        <p className="text-[0.84rem] font-semibold leading-[1.5] text-[var(--color-ink)]">
          Complete the purchase agreement to unlock payment options.
        </p>
      )}

      {loadFailureNotice ?? (
        <form method="post" onSubmit={handleSubmit} className="flex flex-col gap-5">
          {/* Pay-over-time messaging is NOT hand-written here. The Payment Element
              below renders each provider's own approved copy and terms, and paymentMethodOrderFor pins those rails
              directly under the leading method so they are seen. The persuasive
              version of this message belongs beside the PRICE, not here -- see
              components/marketing/pay-over-time.tsx. */}
          <PaymentElement
            options={{ paymentMethodOrder: paymentMethodOrderFor(amountCents) }}
            onLoadError={(event) => onPaymentElementLoadError(event.error?.message || "The Payment Element failed to load.")}
          />

          {formError && (
            <p className="text-[0.88rem] leading-[1.5] text-[var(--color-vermilion)]">{formError}</p>
          )}

          <button
            type="submit"
            disabled={!stripe || !elements || !agreed || !emailValid || submitting}
            className={cn(marketingActionClass({ variant: "accent", size: "lg" }), "w-full justify-center")}
          >
            {submitting ? "Processing…" : payLabel}
          </button>

        </form>
      )}

      {/* Wallets (Apple Pay, Google Pay, Link) render UNDER the Payment Element,
          never above it, so nothing is shown ahead of the bank; the method
          order itself comes from paymentMethodOrderFor. */}
      {!loadFailureNotice && (
        <ExpressCheckoutRow
          paymentIntentId={paymentIntentId}
          clientSecret={clientSecret}
          agreed={agreed}
          onError={setFormError}
          intentKind={intentKind}
        />
      )}

      {/* PayPal is never removed and never ranked ahead of the Payment
          Element — it renders LAST at every price. */}
      {paypalReady && (
        <PayPalCheckoutOption
          source={{ kind: "stripe", paymentIntentId, clientSecret }}
          amountCents={amountCents}
          currency={currency}
          agreed={agreed}
          onError={setFormError}
          onSuccess={onPaypalSuccess}
        />
      )}

      <PaymentTermsFooter />
    </div>
  )
}

/** SECURE + TERMS SIT UNDER EVERY PAYMENT PATH, not between them. His
 *  correction: "Shouldn't the secure encrypted payment and by paying you
 *  agreed to blah blah blah go underneath?" They used to close the card form,
 *  which put them ABOVE the PayPal buttons and read as though they governed
 *  the card only - and the consent they describe applies to paying by any
 *  method here. */
function PaymentTermsFooter() {
  return (
    <>
      {/* Three words are enough, centered. */}
      <p className="inline-flex items-center justify-center gap-2 self-center text-[0.82rem] text-[var(--color-ink-muted)]">
        <ShieldCheck aria-hidden size={15} className="shrink-0 text-[var(--color-vermilion)]" />
        Secure encrypted payment
      </p>

      <p className="self-center text-center text-[0.8rem] leading-[1.5] text-[var(--color-ink-muted)]">
        By paying you agree to the{" "}
        <MarketingLink href="/terms-and-conditions" variant="ghost" size="sm" className={POLICY_LINK_CLASS}>
          Terms &amp; Conditions
        </MarketingLink>
        ,{" "}
        <MarketingLink href="/refund-policy" variant="ghost" size="sm" className={POLICY_LINK_CLASS}>
          Refund Policy
        </MarketingLink>
        ,{" "}
        <MarketingLink href="/success-disclaimer" variant="ghost" size="sm" className={POLICY_LINK_CLASS}>
          Success &amp; Results Disclaimer
        </MarketingLink>
        {" "}and{" "}
        <MarketingLink href="/privacy-policy" variant="ghost" size="sm" className={POLICY_LINK_CLASS}>
          Privacy Policy
        </MarketingLink>
        .
      </p>
    </>
  )
}

/** The right column when Stripe cannot take payments and PayPal stands alone:
 *  the same panel, the same "Payment" label and the same terms footer, with
 *  PayPal's own button group (Pay Later inside it) as the one way to pay. No
 *  card form, no wallet row and no contact field, because none of them can
 *  work without Stripe; PayPal collects and verifies the buyer's email itself.
 *  Must render inside a PayPalScriptProvider. When PayPal's script cannot load
 *  or create-order cannot start an order, onStartFailed hands the buyer back
 *  to the page's fallback. */
export function PayPalOnlyPanel({
  planId,
  request,
  amountCents,
  currency,
  onSuccess,
  onStartFailed,
}: {
  planId: string
  /** What create-order needs beside the plan: the renewal and weekend the link
   *  carried and the acquisition envelope. Never an amount. */
  request: () => Record<string, unknown>
  amountCents: number
  currency: string
  onSuccess: (result: PayPalResult) => void
  onStartFailed: (failure: PayPalStartFailure) => void
}) {
  const [{ isRejected }] = usePayPalScriptReducer()
  const [formError, setFormError] = useState("")

  // PayPal's script never arrived (a blocker, a network filter, PayPal down):
  // there is no button to press, so the buyer goes to the fallback.
  useEffect(() => {
    if (isRejected) onStartFailed({ status: undefined, message: "" })
  }, [isRejected, onStartFailed])

  return (
    <div className="flex flex-col gap-5 rounded-[var(--radius-lg)] border border-[var(--color-rule)] bg-white p-6 md:p-7">
      <SectionEyebrow size="sm" as="span">
        Payment
      </SectionEyebrow>

      <PayPalCheckoutOption
        source={{ kind: "standalone", planId, request }}
        amountCents={amountCents}
        currency={currency}
        agreed
        divider={false}
        onError={setFormError}
        onSuccess={onSuccess}
        onStartFailed={onStartFailed}
      />

      {formError && (
        <p className="text-[0.88rem] leading-[1.5] text-[var(--color-vermilion)]">{formError}</p>
      )}

      <PaymentTermsFooter />
    </div>
  )
}

export function PayPalResultNotice({ result }: { result: PayPalResult }) {
  const [current, setCurrent] = useState(result)
  const [checking, setChecking] = useState(false)
  const [checkMessage, setCheckMessage] = useState("")
  const completed = current.paymentStatus === "completed"

  const checkStatus = useCallback(async () => {
    if (!current.retry || checking) return
    setChecking(true)
    setCheckMessage("")
    try {
      const { res, json } = await requestPayPalCapture(current.retry)
      if (res.status === 200 && json?.status === "COMPLETED") {
        setCurrent({
          paymentStatus: "completed",
          email: typeof json.email === "string" ? json.email : current.email,
          returnPath:
            typeof json.returnPath === "string" ? json.returnPath : current.returnPath,
        })
        return
      }
      if (res.status === 202) {
        setCheckMessage("PayPal is still processing. You can check this same payment again.")
        return
      }
      setCheckMessage(
        json?.error || "We couldn't confirm PayPal's result. Do not submit another payment.",
      )
    } catch {
      setCheckMessage("We couldn't reach PayPal confirmation. Do not submit another payment; check again.")
    } finally {
      setChecking(false)
    }
  }, [checking, current])

  return (
    <div className="flex min-h-[40vh] flex-col items-center justify-center gap-5 py-12 text-center">
      {completed ? (
        <CheckCircle2 className="text-[var(--color-vermilion)]" size={34} aria-hidden />
      ) : (
        <ShieldCheck className="text-[var(--color-vermilion)]" size={34} aria-hidden />
      )}
      <h1 className="serious-display text-[clamp(1.8rem,3.6vw,2.6rem)] text-[var(--color-ink)]">
        {completed ? "Payment completed." : "Payment processing."}
      </h1>
      <p className="max-w-[42ch] text-[0.98rem] leading-[1.6] text-[var(--color-ink-muted)]">
        {completed
          ? `PayPal completed the payment. We are finalizing fulfillment${current.email ? ` and will send the next steps to ${current.email}` : ""}. You do not need to pay again.`
          : "PayPal has not reported a completed payment yet. Do not submit another payment while it finishes processing."}
      </p>
      {!completed && current.retry && (
        <button
          type="button"
          onClick={() => void checkStatus()}
          disabled={checking}
          className={marketingActionClass({ variant: "accent", size: "lg" })}
        >
          {checking ? "Checking…" : "Check PayPal status"}
        </button>
      )}
      {checkMessage && (
        <p className="max-w-[42ch] text-[0.88rem] leading-[1.5] text-[var(--color-ink-muted)]">
          {checkMessage}
        </p>
      )}
      <MarketingLink href="/" variant="accent" size="lg">
        Back to Cold Approach
      </MarketingLink>
    </div>
  )
}
